// Fim de sessão: o token do login (JWT, 2 h no back-end) vence e a plataforma continuava
// navegável com o que já estava em memória, mas sem nenhuma chamada funcionando. Este módulo
// decide QUANDO a sessão acabou; quem a encerra (cookies + volta ao login) é `AppContainer`.
//
// Três sinais, todos sem rede:
// - o `exp` do próprio token, lido sem verificar a assinatura (quem verifica é o back-end);
// - um 401 de uma das APIs da plataforma;
// - um 403 de uma das APIs com o token já vencido. O autorizador do API Gateway responde
//   token vencido com uma política Deny, que vira 403; um 403 com o token VÁLIDO é falta de
//   permissão (admin, obra de outra empresa, apresentação) e não pode deslogar ninguém.

export const SESSION_EXPIRED_PARAM = "sessao";
export const SESSION_EXPIRED_VALUE = "expirada";
export const LOGIN_PATH = "/access";

// Folga para relógios dessincronizados: a sessão acaba um pouco ANTES do back-end recusar.
const CLOCK_SKEW_MS = 30 * 1000;

const decodeBase64Url = (segment) => {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  return atob(padded);
};

/** Instante (ms) em que o token vence, ou `null` quando não é um JWT com `exp` legível. */
export const tokenExpiresAt = (token) => {
  if (typeof token !== "string") return null;
  const [, payload] = token.split(".");
  if (!payload) return null;
  try {
    const { exp } = JSON.parse(decodeBase64Url(payload));
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
};

/** Token ausente, ilegível ou vencido (com a folga de relógio). */
export const isTokenExpired = (token, now = Date.now()) => {
  const expiresAt = tokenExpiresAt(token);
  return expiresAt === null || expiresAt - CLOCK_SKEW_MS <= now;
};

/** Milissegundos até a sessão acabar (0 quando já acabou). */
export const msUntilExpiry = (token, now = Date.now()) => {
  const expiresAt = tokenExpiresAt(token);
  return expiresAt === null ? 0 : Math.max(0, expiresAt - CLOCK_SKEW_MS - now);
};

/** As bases das APIs da plataforma; respostas de outros hosts nunca encerram a sessão. */
export const platformApiBases = (env = process.env) => [
  env.SERVER_URL,
  env.BACKOFFICE_SERVER_URL,
  env.COSTS_SERVER_URL,
  env.BIM_SERVER_URL,
  env.DIRECTOR_SERVER_URL,
  env.AI_SERVER_URL,
].filter(Boolean).map((base) => String(base).replace(/\/$/, ""));

const urlOf = (input) => (typeof input === "string" ? input : input?.url ?? String(input ?? ""));

export const isPlatformApiUrl = (input, bases) => {
  const url = urlOf(input);
  return bases.some((base) => url === base || url.startsWith(`${base}/`));
};

/** A resposta de uma API da plataforma diz que a sessão acabou? */
export const responseEndsSession = ({ input, status, token, bases, now = Date.now() }) => {
  if (!token || !isPlatformApiUrl(input, bases)) return false;
  if (status === 401) return true;
  return status === 403 && isTokenExpired(token, now);
};

/**
 * Envolve `fetch` para avisar `onSessionEnded` quando uma resposta encerra a sessão. A resposta
 * segue para quem chamou do mesmo jeito: a tela que pediu continua tratando o próprio erro.
 * `getToken` lê o token ATUAL; sem token (já deslogado) nada mais dispara.
 */
export const createSessionGuardedFetch = ({ fetchImpl, getToken, onSessionEnded, bases, now = Date.now }) => (
  async (input, init) => {
    const token = getToken();
    const response = await fetchImpl(input, init);
    if (responseEndsSession({ input, status: response?.status, token, bases, now: now() })) {
      onSessionEnded();
    }
    return response;
  }
);

/** O endereço do login que mostra o aviso de sessão expirada. */
export const expiredLoginUrl = () => `${LOGIN_PATH}?${SESSION_EXPIRED_PARAM}=${SESSION_EXPIRED_VALUE}`;

export const cameFromExpiredSession = (search) => (
  new URLSearchParams(search ?? "").get(SESSION_EXPIRED_PARAM) === SESSION_EXPIRED_VALUE
);
